// src/components/ProtocolList.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil, Trash2, RefreshCw } from "lucide-react";

import {
  useGetProtocolsQuery,
  useGetRuntimeProtocolsQuery,
  useDeleteProtocolMutation,
} from "../../redux/protocol/protocolApi";

export default function ProtocolList({ onEdit }) {
  const theme = useTheme();
  const [filter, setFilter] = useState({ status: "", manufacturer: "" });

  const { data, isLoading, error, refetch } = useGetProtocolsQuery(filter);
  const {
    data: runtimeData,
    isLoading: runtimeLoading,
    error: runtimeError,
    refetch: refetchRuntime,
  } = useGetRuntimeProtocolsQuery();
  const [deleteProtocol, { isLoading: isDeleting }] = useDeleteProtocolMutation();

  const protocols = data?.data || [];
  const runtimeProtocols = runtimeData?.data?.runtime || [];

  const handleDelete = async (protocolId, name) => {
    if (!window.confirm(`Delete protocol "${name}"?`)) return;
    try {
      await deleteProtocol(protocolId).unwrap();
    } catch (err) {
      alert("Error: " + (err.data?.error || err.message));
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "protocolId",
        headerName: "Protocol ID",
        width: 170,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            sx={{
              fontWeight: 900,
              borderRadius: 1.6,
              bgcolor: alpha(theme.palette.info.main, 0.08),
            }}
          />
        ),
      },
      {
        field: "runtimePort",
        headerName: "Port",
        width: 110,
        renderCell: (params) => (
          <Typography sx={{ fontWeight: 800 }}>
            {params?.row?.runtimePort ? `:${params.row.runtimePort}` : "-"}
          </Typography>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 230,
      },
      { field: "manufacturer", headerName: "Manufacturer", width: 180, valueGetter: (p) => p || "-" },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => {
          const v = params.value || "inactive";
          const map = {
            active: { bg: alpha(theme.palette.success.main, 0.12), fg: theme.palette.success.main },
            testing: { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.main },
            inactive: { bg: alpha(theme.palette.error.main, 0.10), fg: theme.palette.error.main },
          };
          const st = map[v] || map.inactive;
          return (
            <Chip
              size="small"
              label={v}
              sx={{ fontWeight: 900, borderRadius: 999, bgcolor: st.bg, color: st.fg }}
            />
          );
        },
      },
      {
        field: "connections",
        headerName: "Connections",
        width: 140,
        valueGetter: (_, row) => row?.statistics?.successfulConnections || 0,
      },
      {
        field: "confidence",
        headerName: "Confidence",
        width: 140,
        valueGetter: (_, row) => row?.confidence || 0,
        renderCell: (params) => {
          const v = Number(params.value || 0);
          const color =
            v >= 80 ? theme.palette.success.main : v >= 50 ? theme.palette.warning.main : theme.palette.error.main;
          return (
            <Box sx={{ width: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha(theme.palette.text.primary, 0.08),
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ width: `${v}%`, height: "100%", bgcolor: color }} />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 900, width: 44, textAlign: "right" }}>
                  {v}%
                </Typography>
              </Stack>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit?.(row)}>
                  <Pencil size={16} />
                </IconButton>
              </Tooltip>

              {!row.isBuiltIn && (
                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isDeleting}
                      onClick={() => handleDelete(row.protocolId, row.name)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [theme, onEdit, isDeleting]
  );

  const rows = useMemo(() => {
    const dbByRuntimeKey = new Map(
      protocols.map((p) => [
        String(p?.runtimeBoundProtocol || p?.protocolId || "").trim().toLowerCase(),
        p,
      ])
    );

    if (runtimeProtocols.length) {
      return runtimeProtocols
        .map((runtime) => {
          const key = String(runtime?.key || "").trim().toLowerCase();
          if (!key) return null;
          const db = dbByRuntimeKey.get(key) || null;
          return {
            id: db?._id || key,
            _id: db?._id,
            protocolId: db?.protocolId || key,
            name: db?.name || runtime?.displayName || runtime?.name || key,
            manufacturer: db?.manufacturer || "-",
            status: db?.status || "active",
            confidence: db?.confidence || 100,
            statistics: db?.statistics || { successfulConnections: 0 },
            runtimePort: runtime?.port ?? db?.runtimePort ?? null,
            runtimeBoundProtocol: key,
            supportedModels: Array.isArray(runtime?.supportedModels) ? runtime.supportedModels : [],
            isBuiltIn: db?.isBuiltIn ?? true,
          };
        })
        .filter(Boolean);
    }

    return protocols.map((p) => ({ id: p._id, ...p }));
  }, [protocols, runtimeProtocols]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const statusMatch = !filter.status || String(row?.status || "").toLowerCase() === String(filter.status).toLowerCase();
      const manufacturerNeedle = String(filter.manufacturer || "").trim().toLowerCase();
      const manufacturerMatch =
        !manufacturerNeedle ||
        String(row?.manufacturer || "").toLowerCase().includes(manufacturerNeedle);
      return statusMatch && manufacturerMatch;
    });
  }, [rows, filter]);

  return (
    <Box>
      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "end" }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1 }}>
            <TextField
              select
              label="Status"
              value={filter.status}
              onChange={(e) => setFilter((p) => ({ ...p, status: e.target.value }))}
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="testing">Testing</MenuItem>
            </TextField>

            <TextField
              label="Manufacturer"
              value={filter.manufacturer}
              onChange={(e) => setFilter((p) => ({ ...p, manufacturer: e.target.value }))}
              size="small"
              placeholder="e.g., Concox"
              sx={{ flex: 1, minWidth: 220 }}
            />
          </Stack>

          <Button
            onClick={() => {
              refetch();
              refetchRuntime();
            }}
            variant="outlined"
            startIcon={<RefreshCw size={16} />}
            sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {(error || runtimeError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.error || runtimeError?.data?.error || "Failed to load protocols"}
        </Alert>
      )}

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
          <Typography sx={{ fontWeight: 900 }}>
            Protocols <Typography component="span" color="text.secondary">({filteredRows.length})</Typography>
          </Typography>
        </Box>

        <Box sx={{ height: 520 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={isLoading || runtimeLoading}
            disableRowSelectionOnClick
            density="standard"
            pageSizeOptions={[10, 20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                fontWeight: 900,
              },
              "& .MuiDataGrid-cell": { outline: "none" },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
